import { useEffect } from 'react'
import { homePageView } from 'tag-manager/page_view'
import { Seo } from 'ui-components/components/atoms/Seo'

import {
  Dna,
  FolderOpen,
  Mailbox,
  MapTrifold,
  StarFour
} from '@phosphor-icons/react'

import { Cta } from 'web-pages/v2/components/Cta'
import { Customers } from 'web-pages/v2/components/Customers'
import { DemoVideo } from 'web-pages/v2/components/DemoVideo'
import { Footer } from 'web-pages/v2/components/Footer'
import { GetInTouch } from 'web-pages/v2/components/GetInTouch'
import { Header } from 'web-pages/v2/components/Header'
import { Heading, Hero } from 'web-pages/v2/components/Hero'
import { HowItWork, HowItWorks } from 'web-pages/v2/components/HowItWorks'
import { Testimonials } from 'web-pages/v2/components/Testimonials'

import AutoMagicMapping from 'web-pages/v2/assets/auto-magic-mapping.webp'
import EnrichWithContext from 'web-pages/v2/assets/enrich-with-context.webp'
import InstantVisualisation from 'web-pages/v2/assets/instant-visualisation.webp'
import SmartDataImport from 'web-pages/v2/assets/smart-data-import.webp'
import SmartSharing from 'web-pages/v2/assets/smart-sharing.webp'

// prettier-ignore
const seo = {
  title: 'Mapstack | Magically Create Stunning Maps with AI',
  description: 'Mapstack is the easiest way to turn places, addresses, regions, customers, sales, resources, and assets into interactive maps. No experience needed — just upload, map, and share.',
  keywords: 'interactive maps, AI mapping tool, map generator, no-code mapping, business mapping, map from spreadsheet, location data visualisation, customer mapping, create map online, Mapstack'
}

export function Home() {
  useEffect(() => {
    homePageView()
  }, [])

  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        openGraph={{
          title: seo.title,
          description: seo.description,
          images: [
            {
              url: 'https://mapstack.io/images/og_image.webp',
              secureUrl: 'https://mapstack.io/images/og_image.webp',
              alt: 'Mapstack - Visualize and Map Location Data Instantly',
              type: 'image/webp',
              width: 1200,
              height: 630
            }
          ],
          url: 'https://mapstack.io/',
          siteName: 'Mapstack'
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: seo.keywords
          }
        ]}
      />

      <Header />

      <div className='bg-gray-50'>
        <Hero
          // prettier-ignore
          textProps={{
            heading: <Heading />,
            subheading: 'Mapstack is the AI-powered mapping platform for business that helps your team create interactive maps in seconds.',
            cta: 'Make your free map',
            ctaCaption: <CtaCaption />
          }}
        />

        <Customers />

        <HowItWorks
          // prettier-ignore
          textProps={{
            heading: 'Turn your locations, lists and business data into interactive maps in seconds — no stress, no setup.'
          }}
        >
          <HowItWork
            icon={FolderOpen}
            eyebrow='Smart Data Import'
            heading='Bring whatever you’ve got'
            description='Upload a file, paste a list, or connect tools like Stripe, Shopify, or Xero — we’ll handle the rest.'
            cta='Turbo Charge Your Data'
            href='/sign-in'
            src={SmartDataImport}
          />

          <HowItWork
            reverse
            icon={StarFour}
            eyebrow='Auto-Magic Mapping'
            heading='We plot it for you'
            description='Mapstack finds places, regions, and patterns automatically — no coding, no GIS skills.'
            cta='Dive into the Magic'
            href='/sign-in'
            src={AutoMagicMapping}
          />

          <HowItWork
            icon={MapTrifold}
            eyebrow='Instant Visualisation'
            heading='See the patterns'
            description='We automatically highlight what matters — like clusters, hotspots, and groupings — without you lifting a finger.'
            cta='Uncover the Big Picture'
            href='/sign-in'
            src={InstantVisualisation}
          />

          <HowItWork
            reverse
            icon={Dna}
            eyebrow='Enrich with Context'
            heading='Add extra insight, instantly'
            description='Overlay demographic data, regions, or points of interest — with one click.'
            cta='See Data In Context'
            href='/sign-in'
            src={EnrichWithContext}
          />

          <HowItWork
            icon={Mailbox}
            eyebrow='Smart Sharing'
            heading='Show it off (or keep it private)'
            description='Share your map with your team, embed it in a site, export an infographic, or keep it for internal use.'
            cta='Start Showing Off'
            href='/sign-in'
            src={SmartSharing}
          />
        </HowItWorks>

        <DemoVideo
          textProps={{
            heading: 'Now watch how easy it is!'
          }}
        />

        <Testimonials />

        <Cta
          textProps={{
            heading: 'Ready to make your first map?',
            cta: 'Make your free map'
          }}
        />

        <GetInTouch />

        <Footer />
      </div>
    </>
  )
}

function CtaCaption() {
  return (
    <>
      It’s super easy & <br />
      no credit card required!
    </>
  )
}

export const getServerSideProps = () => {
  return {
    props: {}
  }
}
